import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import { img } from "../../lib/utils-app";
import { ImageUploader } from "../../components/ImageUploader";

export function Profile() {
  const { user, updateProfile } = useAuth();
  const { t, lang } = useLang();
  const es = lang === "es";
  const isAgent = user.role === "agent" || user.role === "admin";

  const [form, setForm] = useState({ name: user.name, phone: user.phone || "" });
  const [saving, setSaving] = useState(false);

  const [agent, setAgent] = useState(null);
  const [agentLoading, setAgentLoading] = useState(isAgent);
  const [agentSaving, setAgentSaving] = useState(false);

  useEffect(() => {
    if (!isAgent) return;
    const fallback = { name: user.name, title: "", phone: user.phone || "", photo: "", bio: "" };
    api.get("/agents/me")
      .then(({ data }) => setAgent(data || fallback))
      .catch(() => setAgent(fallback))
      .finally(() => setAgentLoading(false));
  }, [isAgent]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile(form);
    setSaving(false);
    res.ok ? toast.success(es ? "Perfil actualizado" : "Profile updated") : toast.error(res.error || "Error");
  };

  const setA = (k, v) => setAgent((a) => ({ ...a, [k]: v }));
  const saveAgent = async (e) => {
    e.preventDefault();
    setAgentSaving(true);
    try {
      const { data } = await api.put("/agents/me", agent);
      setAgent(data);
      toast.success(es ? "Perfil público guardado" : "Public profile saved");
    } catch { toast.error("Error"); } finally { setAgentSaving(false); }
  };

  const inp = "w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors";
  const lbl = "label-caps text-muted-foreground";

  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-4xl font-light mb-8">{t("dash.profile")}</h1>
      <div className="flex items-center gap-4 mb-8">
        {agent?.photo ? (
          <img src={img(agent.photo)} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <span className="w-16 h-16 rounded-full bg-primary/15 text-primary grid place-items-center font-serif text-2xl">{user.name?.[0]}</span>
        )}
        <div><p className="font-serif text-2xl">{user.name}</p><p className="text-muted-foreground text-sm capitalize">{user.role}</p></div>
      </div>

      <form onSubmit={save} className="space-y-4" data-testid="profile-form">
        <div><label className={lbl}>{t("auth.name")}</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="profile-name" /></div>
        <div><label className={lbl}>{t("auth.email")}</label><input value={user.email} disabled className={inp + " opacity-60"} /></div>
        <div><label className={lbl}>{t("auth.phone")}</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} data-testid="profile-phone" /></div>
        <button disabled={saving} className="px-6 py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="profile-save">{saving ? t("common.sending") : t("common.save")}</button>
      </form>

      {isAgent && (
        <div className="mt-12 pt-8 border-t border-border" data-testid="agent-profile-section">
          <h2 className="font-serif text-2xl font-light mb-1">{es ? "Mi perfil público de agente" : "My public agent profile"}</h2>
          <p className="text-sm text-muted-foreground mb-6">{es ? "Esto es lo que ven los clientes en la página de agentes." : "This is what clients see on the agents page."}</p>
          {agentLoading ? (
            <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>
          ) : (
            <form onSubmit={saveAgent} className="space-y-4" data-testid="agent-profile-form">
              <div><label className={lbl}>{es ? "Foto" : "Photo"}</label><div className="mt-2"><ImageUploader single aspect={1} value={agent?.photo ? [agent.photo] : []} onChange={(a) => setA("photo", a[0] || "")} lang={lang} /></div></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>{es ? "Nombre visible" : "Display name"}</label><input value={agent?.name || ""} onChange={(e) => setA("name", e.target.value)} className={inp} data-testid="agent-me-name" /></div>
                <div><label className={lbl}>{es ? "Título / cargo" : "Title / role"}</label><input value={agent?.title || ""} onChange={(e) => setA("title", e.target.value)} className={inp} data-testid="agent-me-title" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>{es ? "Teléfono" : "Phone"}</label><input value={agent?.phone || ""} onChange={(e) => setA("phone", e.target.value)} className={inp} data-testid="agent-me-phone" /></div>
                <div><label className={lbl}>WhatsApp</label><input value={agent?.whatsapp || ""} onChange={(e) => setA("whatsapp", e.target.value)} placeholder="+1 305 555 0100" className={inp} data-testid="agent-me-whatsapp" /></div>
              </div>
              <div><label className={lbl}>Bio</label><textarea rows={4} value={agent?.bio || ""} onChange={(e) => setA("bio", e.target.value)} className={inp + " resize-none"} data-testid="agent-me-bio" /></div>
              <button disabled={agentSaving} className="px-6 py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="agent-me-save">{agentSaving ? (es ? "Guardando…" : "Saving…") : (es ? "Guardar perfil" : "Save profile")}</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
